import type { FC } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { Reveal } from '@/components/kit/reveal'
import { milestones } from '@/constants/about'

/**
 * Milestones on a vertical line that fills with orange as the page scrolls.
 */
export const Timeline: FC = () => {
    const listRef = useRef<HTMLOListElement>(null)
    const fillRef = useRef<HTMLSpanElement>(null)

    useScrollFrame(() => {
        const list = listRef.current
        const fill = fillRef.current

        if (!list || !fill) return

        const rect = list.getBoundingClientRect()
        const reading = window.innerHeight * 0.6
        const progress = Math.min(1, Math.max(0, (reading - rect.top) / rect.height))

        fill.style.transform = `scaleY(${progress.toFixed(4)})`

        list.querySelectorAll<HTMLElement>('[data-milestone]').forEach(item => {
            item.dataset.passed = String(item.getBoundingClientRect().top < reading)
        })
    })

    return (
        <ol
            ref={listRef}
            className='relative'
        >
            <span
                aria-hidden='true'
                className='absolute bottom-0 left-[7px] top-2 w-px bg-ink/12 md:left-1/2'
            />
            <span
                ref={fillRef}
                aria-hidden='true'
                className='absolute bottom-0 left-[7px] top-2 w-[2px] origin-top -translate-x-[0.5px] bg-ember md:left-1/2'
                style={{ transform: 'scaleY(0)' }}
            />

            {milestones.map((milestone, index) => {
                const right = index % 2 === 1

                return (
                    <li
                        key={milestone.year}
                        data-milestone=''
                        data-passed='false'
                        className='group relative pb-14 pl-10 last:pb-0 md:grid md:grid-cols-2 md:gap-16 md:pl-0'
                    >
                        <span
                            aria-hidden='true'
                            className='absolute left-0 top-3 size-[15px] rounded-full border-[3px] border-cream bg-ink/20 transition-[background-color,scale] duration-500 group-data-[passed=true]:scale-125 group-data-[passed=true]:bg-ember md:left-1/2 md:-translate-x-1/2'
                        />

                        <Reveal
                            delay={80}
                            className={cn(right ? 'md:col-start-2' : 'md:text-right')}
                        >
                            <p className='font-heading text-[clamp(2.75rem,5vw,4.25rem)] font-extrabold leading-none tracking-[-0.04em] text-ink/25 transition-colors duration-700 group-data-[passed=true]:text-ink'>
                                {milestone.year}
                            </p>
                            <h3 className='mt-3 text-2xl font-extrabold tracking-[-0.04em]'>
                                {milestone.title}
                            </h3>
                            <p className={cn('mt-2 max-w-sm text-base leading-relaxed text-stone', !right && 'md:ml-auto')}>
                                {milestone.body}
                            </p>
                        </Reveal>
                    </li>
                )
            })}
        </ol>
    )
}
